import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SearchService } from '@journal/core/api/search/search.service';
import { FollowerService } from '@sparro/app/core/api/follower/follower.service';
import { ALLOWED_HOSTS, UNI_CONSTANTS } from '@sparro/app/core/config/constants.config';
import { MetaService } from '@sparro/app/core/helpers/meta/meta.service';
import { SnackbarService } from '@sparro/app/core/helpers/toastr/snackbar.service';
import { Blog } from '@sparro/app/core/models/blog.model';
import { Report } from '@sparro/app/core/models/report.model';
import { User } from '@sparro/app/core/models/user.model';
import { ConfirmationDialogComponent } from '@sparro/app/shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ReportDialogComponent } from '@sparro/app/shared/components/dialogs/report-dialog/report-dialog.component';
import { ShareDialogComponent } from '@sparro/app/shared/components/dialogs/share-dialog/share-dialog.component';
import { BlogService } from '@sparro/core/api/blog/blog.service';
import { BookmarkService } from '@sparro/core/api/bookmark/bookmark.service';
import { UserService } from '@sparro/core/api/user/user.service';
import { environment } from '@sparro/env/environment';

@Component({
  selector: 'uni-blog-card-header',
  templateUrl: './blog-card-header.component.html',
  styleUrls: ['./blog-card-header.component.scss']
})
export class BlogCardHeaderComponent implements OnInit, AfterViewInit {
  @Input() headerType: string = 'feed';
  @Input() blog: Blog;
  @Input() cardWidth: number = null;
  @Input() self: boolean = false;
  @Input() host: ALLOWED_HOSTS;
  @Input() showCardOptions: boolean;
  @Output() delete = new EventEmitter();
  blogCopyLink: string;
  source: string = 'blogs';
  published: boolean;
  user: User;

  icons = {
    bookmark: 'https://uni-common-assets.s3.amazonaws.com/icons/system/svg/1/addtocollection/default-48x8.svg',
    bookmark_fill: 'https://uni-common-assets.s3.amazonaws.com/icons/system/svg/1/addtocollection/fill-48x48.svg',
  };

  constructor(
    private metaService: MetaService,
    public snackbarService: SnackbarService,
    private blogService: BlogService,
    public dialog: MatDialog,
    public changeDetectorRef: ChangeDetectorRef,
    public router: Router,
    private searchService: SearchService,
    private userService: UserService,
    private followerService: FollowerService,
    private bookmarkService: BookmarkService,
  ) { }

  ngOnInit() {
    if (this.host === UNI_CONSTANTS.HOSTS.EVALUATION || this.host === UNI_CONSTANTS.HOSTS.BUILDER) {
      this.headerType = 'none';
    }
    this.published = this.blog.status === 'PUBLISH' ? true : false;
    this.getAuthUser();
  }

  ngAfterViewInit() {
    this.blogCopyLink = this.metaService.copyItem('journal', this.blog.slug);
    this.changeDetectorRef.detectChanges();
  }

  getAuthUser(): void {
    this.userService.userInitialized.subscribe(
      (initialized: boolean) => {
        if (initialized) {
          this.userService.user.subscribe(
            (user: User) => {
              this.user = user;
            });
        }
      });
  }

  editBlog() {
    window.open(`${environment.postUrl}edit/${this.blog.uuid}`, '_blank');
  }

  deleteBlog() {
    this.delete.emit();
  }

  copiedLink(link: string) {
    this.snackbarService.success(`'${this.blog.title}' journal's link has been copied!`);
  }

  toggleUnlist() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      // width: '350px',
      // height: '180px',
      data: {
        message: `<p style="text-align:center">
        Are you sure you want to make this journal private?</p>`,
        okayButtonText: 'Yes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      // console.log(result);
      if (result) {
        this.blogService.toggleUnlist(this.blog.uuid).subscribe(
          (response: any) => {
            // console.log(response);
            if (response) {
              this.blog.unlisted = !this.blog.unlisted;
            }
            // TODO: Add snackbarService
          }, (err) => {
            console.log(err);
          });
      } else {

      }
    });
  }

  getDisciplineFilter(discipline) {
    this.searchService.setSearchDiscipline(discipline);
    this.router.navigate(['search']);
  }

  goToProfile(blog: Blog): string {
    const url = `${environment.profileUrl}${blog.author.username}/`;
    return url;
  }

  toggleFollow() {
    if (this.blog.is_following) {
      this.followerService.unfollow(this.source, this.blog.slug, this.blog.is_following?.id).subscribe(
        (response) => {
          if (response) {
            this.blog.is_following = null;
            this.changeDetectorRef.detectChanges();
            this.snackbarService.success(`You have unfollowed <b>${this.blog.title}</b>`);
          }
        });
    } else {
      const data = {
        follower: this.user.id
      };
      this.followerService.follow(this.source, this.blog.slug, data).subscribe(
        (response: any) => {
          // console.log(response);
          this.blog.is_following = response;
          this.changeDetectorRef.detectChanges();
          this.snackbarService.success(`You have started following <b>${this.blog.title}</b>`);
        },
        (err) => {
          console.log(err);
        });
    }
  }

  toggleBookmark() {
    if (!this.user) {
        this.snackbarService.error('To perform this action, login or sign-up');
        this.redirectToLogin();
    } else {
      if (this.blog.bookmark) {
        this.bookmarkService.removeFromCollection(this.source, this.blog.slug, this.blog.bookmark?.id).subscribe(
          (response: any) => {
            if (response) {
              this.bookmarkService.removeBookmarks(this.blog);
              this.blog.bookmark = null;
              this.snackbarService.success('Removed from collection');
            }
          });
      } else {
        const data = {
          user: this.user.id
        };
        this.bookmarkService.addToCollection(this.source, this.blog.slug, data).subscribe(
          (response: any) => {
            this.blog.bookmark = response;
            this.snackbarService.success('Added to collection');
          },
          (err) => {
            console.log(err);
          }
        );
      }
    }
  }

  createReport() {
    if (!this.user) {
      this.redirectToLogin();
      return;
    } else {
      const dialogWidth = window.innerWidth <= 540 ? `90vw` : `527px`;
      const dialogRef = this.dialog.open(ReportDialogComponent, {
        width: dialogWidth,
        maxWidth: dialogWidth,
        data: {
          user: this.user,
          slug: this.blog.slug,
          source: this.source,
          url: `${environment.postUrl}${this.blog.slug}`
        }
      });
      dialogRef.afterClosed().subscribe(
        (response: any) => {
          if (response) {
            this.blog.report = response;
            this.snackbarService.success('Report has been initiated');
          }
        });
    }
  }

  redactReport() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '420px',
      data: {
        message: `This will withdraw the report and the action cannot be undone.
        Are you sure you want to continue?`,
        okayButtonText: 'Yes'
      }
    });
    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result) {
          Report.redactReport(this.source, this.blog.slug, this.blog.report.id).subscribe(
            (response: any) => {
              if (response) {
                this.blog.report = response;
                this.snackbarService.success('Report has been redacted');
              }
            });
        }
      });
  }

  redirectToLogin() {
    window.location.href = `${environment.loginUrl}authentication?service=sign-in&continue=${window.location.href}`;
  }

  openShareDialog() {
    const dialogRef = this.dialog.open(ShareDialogComponent, {
      data: {
        sourceCopy: 'journal',
        slug: this.blog.slug,
        content: {
          title: this.blog.title,
          subtitle: this.blog.tagline,
          cover_image: this.blog.image
        }
      }
    });
    dialogRef.afterClosed().subscribe(
      (response) => {

      });
  }
}
